import React, { useEffect, useState, useCallback } from "react";
import {
  CCard,
  CCardBody,
  CCardHeader,
  CButton,
  CSpinner,
  CBadge,
} from "@coreui/react";
import CIcon from "@coreui/icons-react";
import { cilBolt } from "@coreui/icons";
import { getHermesInsight } from "../../api/hermesApi";
import ErrorAlert from "../UI/Status/ErrorAlert";

export default function HermesPanel({
  serverId,
  dateFrom,
  dateTo,
  groupByRestaurant = false,
  restaurantGroup = "",
}) {
  const [insight, setInsight] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Has the user explicitly asked for analysis of the CURRENTLY selected period —
  // GigaChat calls cost tokens, so this must only ever happen on a click, never on mount
  // or on a filter change.
  const [requested, setRequested] = useState(false);

  const fetchInsight = useCallback(
    (force) => {
      if (!serverId || !dateFrom || !dateTo) return;
      setRequested(true);
      setLoading(true);
      setError(null);
      getHermesInsight({
        id: serverId,
        dateFrom,
        dateTo,
        groupByRestaurant,
        restaurantGroup,
        force,
      })
        .then((data) => setInsight(data))
        .catch((e) => setError(e))
        .finally(() => setLoading(false));
    },
    [serverId, dateFrom, dateTo, groupByRestaurant, restaurantGroup],
  );

  // The period/server/group changed under us — the shown analysis (if any) is for a
  // different selection now, so clear it and go back to "not yet requested". We never
  // auto-fetch here; the user has to press the button again for the new period.
  useEffect(() => {
    setInsight(null);
    setError(null);
    setRequested(false);
  }, [serverId, dateFrom, dateTo, groupByRestaurant, restaurantGroup]);

  if (!serverId) return null;

  const content = insight?.content;

  return (
    <CCard className="mb-4 hermes-panel">
      <CCardHeader className="d-flex align-items-center justify-content-between py-2">
        <div className="d-flex align-items-center gap-2">
          <CIcon icon={cilBolt} className="text-warning" />
          <span className="fw-semibold">Анализ Hermes</span>
          {insight?.cached && (
            <CBadge color="secondary" title={insight.generated_at}>
              кэш
            </CBadge>
          )}
        </div>
        <CButton
          size="sm"
          color="primary"
          variant="outline"
          onClick={() => fetchInsight(requested)}
          disabled={loading || !dateFrom || !dateTo}
        >
          {loading ? <CSpinner size="sm" /> : requested ? "Обновить анализ" : "Запустить анализ"}
        </CButton>
      </CCardHeader>
      <CCardBody className="pt-3">
        <ErrorAlert error={error} title="Не удалось получить анализ" />

        {insight?.date_clamped && (
          <div className="small text-body-secondary mb-3">
            Сегодняшний день ещё не завершён и исключён из анализа — период скорректирован до{" "}
            {insight.date_to}.
          </div>
        )}

        {!requested && !loading && (
          <div className="text-body-secondary py-2">
            Нажмите «Запустить анализ», чтобы получить AI-разбор отчёта за выбранный период.
          </div>
        )}

        {loading && !content && (
          <div className="d-flex align-items-center gap-2 text-body-secondary py-2">
            <CSpinner size="sm" />
            Hermes анализирует отчёт…
          </div>
        )}

        {content && (
          <>
            <p className="mb-3">{content.summary}</p>

            {content.highlights?.length > 0 && (
              <div className="mb-3">
                <div className="small fw-semibold text-body-secondary mb-1">Наблюдения</div>
                <ul className="mb-0 ps-3">
                  {content.highlights.map((h, i) => (
                    <li key={i}>{h}</li>
                  ))}
                </ul>
              </div>
            )}

            {content.risks?.length > 0 && (
              <div className="mb-3">
                <div className="small fw-semibold text-danger mb-1">Риски</div>
                <ul className="mb-0 ps-3">
                  {content.risks.map((r, i) => (
                    <li key={i}>{r}</li>
                  ))}
                </ul>
              </div>
            )}

            {content.recommendations?.length > 0 && (
              <div>
                <div className="small fw-semibold text-success mb-1">Рекомендации</div>
                <ul className="mb-0 ps-3">
                  {content.recommendations.map((r, i) => (
                    <li key={i}>{r}</li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </CCardBody>
    </CCard>
  );
}
